import { EVENTS } from '../_constants';
import { MainNavItemWithSubNav } from './mainNavItemWithSubNav';

/**
 * Manages the main navigation and all its sub-navigation items
 */
class MainNav {
    /**
     * Creates a new MainNav instance
     * @param {HTMLElement} mainNavEl - The main navigation container element
     */
    constructor(mainNavEl) {
        /** @type {HTMLElement} */
        this.mainNavEl = mainNavEl;

        /** @type {HTMLElement[]} */
        this.mainNavItems = Array.from(
            this.mainNavEl.querySelectorAll('.main-nav__item[data-nav-item-level="1"]')
        );

        /** @type {HTMLElement[]} */
        this.mainNavItemsWithSubNav = this.mainNavItems.filter(item =>
            item.classList.contains('main-nav__item--has-subnav')
        );

        /** @type {MainNavItemWithSubNav[]} */
        this.instances = this.mainNavItemsWithSubNav.map(item => {
            if (!item.mainNavItemWithSubNavInstance) {
                item.mainNavItemWithSubNavInstance = new MainNavItemWithSubNav(item);
            }
            return item.mainNavItemWithSubNavInstance;
        });

        document.addEventListener(EVENTS.VIEWPORT_CHANGE, (e) => {
            const { isMobile } = e.detail;

            this._updateAriaHidden(isMobile);

            if (isMobile) {
                this._enableMobile();
            } else {
                this._disableMobile();
            }
        });
    }

    /**
     * Enables mobile behavior for all navigation items
     * @private
     */
    _enableMobile() {
        this.instances.forEach(instance => {
            instance.close();
            instance.enable();
        });
    }

    /**
     * Disables mobile behavior and resets all navigation items
     * @private
     */
    _disableMobile() {
        this.instances.forEach(instance => {
            instance.close();
            instance.disable();
        });
    }

    /**
     * Ensures the main navigation is visible to assistive technologies on desktop
     * @param {boolean} isMobile - Whether the current viewport is mobile
     * @private
     */
    _updateAriaHidden(isMobile) {
        if (!isMobile) {
            this.mainNavEl.setAttribute('aria-hidden', 'false');
        }
    }
}

/**
 * Initializes the main navigation functionality
 * Creates a MainNav instance if a main navigation element exists
 */
function initMainNav() {
    const mainNavEl = document.querySelector('.main-nav');

    // Guard: exit if no nav element or already initialized
    if (!mainNavEl || mainNavEl.mainNavInstance) return;

    mainNavEl.mainNavInstance = new MainNav(mainNavEl);
}

// Initialize when DOM is ready
if (document.readyState !== "loading") {
    initMainNav();
} else {
    document.addEventListener("DOMContentLoaded", initMainNav);
}
